<script setup>

import { computed, ref, onMounted, onUnmounted } from 'vue';
import App from '@/Layouts/App.vue';
import ButtonSendCV from '@/Components/Career/ButtonSendCV.vue';
import Border from "@/Components/Career/Border.vue";
import ContentCareer from "@/Components/Career/ContentCareer.vue";
import CreateSolutionSection from "@/Components/Career/CreateSolutionSection.vue";
import Button from "@/Components/Button.vue";
import imageHelper from '@/Helpers/image_helper.js';
import { usePage } from "@inertiajs/vue3";

const { getImage } = imageHelper.methods;

const setting = computed(() => usePage().props.setting);

const props = defineProps({
    benefit: Array,
    development: Array,
    career: Array,
    detailCareer: Array,
    specialWork: Array,
});

const isOpenSheet = ref(false);
const isMobile = ref(false);
const selectedId = ref(null);
const activeSheet = ref(null);

const openSheet = (id) => {
    if (activeSheet.value === id) {
        closeSheet();
    } else {
        selectedId.value = id;
        activeSheet.value = id;
        isOpenSheet.value = true;
    }
};

const closeSheet = () => {
    selectedId.value = null;
    isOpenSheet.value = false;
    activeSheet.value = null;
};

const selectedWork = computed(() => {
    return props.specialWork.find(item => item.id === selectedId.value) || null;
});

const handleClickOutside = (event) => {
    const sheetContent = event.target.closest(".sheet-content");
    const sheetToggle = event.target.closest(".sheet-toggle");
    if (isOpenSheet.value && !sheetContent && !sheetToggle) {
        closeSheet();
    }
};

const settingDevelopment = computed(() => {
    return setting.value.filter(item => item.key === 'development');
});

const moreBenefits = computed(() => {
    return setting.value.filter(item => item.key === 'more_benefits');
});

const yourCv = computed(() => {
    return setting.value.filter(item => item.key === 'your_cv');
});

onMounted(() => {
    document.addEventListener("click", handleClickOutside);
});

onUnmounted(() => {
    document.removeEventListener("click", handleClickOutside);
});

const checkScreenSize = () => {
    isMobile.value = window.innerWidth < 1024;
};

onMounted(() => {
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
});

onUnmounted(() => {
    window.removeEventListener('resize', checkScreenSize);
});

const parseHtml = (html) => {
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = html;

    return Array.from(tempDiv.childNodes)
        .filter((node) => node.nodeType === 1)
        .map((node) => {
            const tag = node.nodeName.toLowerCase();
            const content = node.innerHTML;

            const containsUrl =
                tag === "p" &&
                /(?:https?:\/\/[^\s]+|mailto:[^\s]+)/gi.test(content);

            return {
                tag,
                content,
                containsUrl,
            };
        });
};

const processedDetails = computed(() =>
    props.detailCareer.map((item) => ({
        parsedContent: parseHtml(item.description),
    }))
);
</script>

<template>
    <App>
        <template v-for="item in career">
            <section class="relative bg-cover bg-center w-full height-banners"
                :style="{ backgroundImage: `url(${getImage(item.image_banner)})` }">
                <div class="absolute top-0 w-full h-full flex flex-col justify-center items-center gap-y-1 p-3 md:p-0">
                    <h1 class="text-title text-primaryColor text-center">{{ item.title }}</h1>
                    <p class="text-description text-grayBlackColor text-center width-description-banner">
                        {{ item.description }}
                    </p>
                </div>
            </section>
        </template>

        <section class="content-body content-padding">
            <div class="w-full flex mt-5 md:mt-10" :class="{ 'flex-col': isMobile }">
                <div class="w-full xl:w-[40%]">
                    <div class="w-full flex flex-col justify-start">
                        <div class="flex justify-start items-center gap-x-2 mb-3 md:mb-5">
                            <h1 class="text-primary text-grayBlackColor font-bold">ការងារពិសេស</h1>
                        </div>

                        <div class="flex flex-col gap-y-0 md:gap-y-5">
                            <template v-if="!isMobile" v-for="(item, index) in specialWork" :key="index">
                                <button
                                    class="flex h-full gap-x-2 sheet-toggle py-1 transition-colors duration-300 ease-in-out"
                                    @click="openSheet(item.id)" :class="{
                                        'bg-gray-100': activeSheet === item.id && isOpenSheet
                                    }">
                                    <Border />
                                    <div class="h-full flex flex-col justify-start gap-y-1 pt-2">
                                        <h2 class="text-title-content-sub leading-none text-start">{{ item.title }}</h2>
                                        <p class="text-colorDescriptionCareer text-start text-description">
                                            {{ item.sub_title }}
                                        </p>
                                    </div>
                                </button>
                            </template>

                            <template v-if="isMobile" v-for="(item, index) in specialWork" :key="index">
                                <button
                                    class="flex justify-between items-center h-full gap-x-2 sheet-toggle py-1.5 md:py-1 transition-colors duration-300 ease-in-out border-b border-gray-200"
                                    :class="{
                                        'border-t': index === 0,
                                        'bg-gray-100': activeSheet === item.id && isOpenSheet
                                    }" @click="openSheet(item.id)">
                                    <div class="w-full flex justify-start items-center gap-3">
                                        <h1 class="text-[45px] font-black text-primaryColor/30 leading-none">
                                            {{ index + 1 }}</h1>

                                        <div class="h-full flex flex-col justify-start gap-y-1 pt-2">
                                            <h2 class="text-title-content-sub leading-none text-start">
                                                {{ item.title }}
                                            </h2>
                                            <p class="text-colorDescriptionCareer text-start text-description">
                                                {{ item.sub_title }}
                                            </p>
                                        </div>
                                    </div>

                                    <i
                                        class="ri-arrow-right-wide-line text-end w-3 text-[20px] text-colorDescriptionCareer"></i>
                                </button>
                            </template>

                            <div v-if="isOpenSheet" class="fixed z-50 inset-0 bg-black/50 overlay-bg"
                                @click="closeSheet"></div>

                            <div class="z-50 fixed top-0 right-0 w-[88%] md:w-4/5 xl:w-3/5 2xl:w-2/5 h-screen bg-white shadow-lg transition-transform transform sheet-content duration-200 ease-in-out"
                                :class="{ 'translate-x-full': !isOpenSheet, 'translate-x-0': isOpenSheet }">
                                <button class="absolute z-50 top-3 right-3 text-gray-700 border rounded-full w-9 h-9"
                                    @click="closeSheet">✖</button>
                                <ContentCareer v-if="selectedWork" :specialWork="[selectedWork]" />
                            </div>
                        </div>
                    </div>
                </div>

                <div class="w-full xl:w-[60%] mt-5 xl:mt-0 space-y-2">
                    <!--== Content Requirements Company -->
                    <div v-for="(item, index) in detailCareer" :key="index" class="w-full grid justify-start gap-y-2">
                        <h1 class="text-title-content-sub">{{ item.title }}</h1>

                        <template v-for="(element, idx) in processedDetails[index].parsedContent" :key="idx">
                            <component :is="element.tag" v-html="element.content" :class="{
                                'tag-p': element.tag === 'p', // tag p
                                'text-primaryColor': element.tag === 'p' && element.containsUrl, // tag p have url.
                                'tag-ul list-disc pl-10 space-y-0 md:space-y-1 leading-7 md:leading-[30px]': element.tag === 'ul', // tag ul
                            }"></component>
                        </template>
                    </div>
                </div>
            </div>
        </section>

        <hr class="w-full border-0 my-5 md:my-10 bg-gradient-to-r from-[#FFFFFF] via-[#E0E0E0] to-[#FFFFFF] h-[1px]">

        <section class="content-body content-padding mb-5 md:mb-10">
            <template v-for="item in moreBenefits">
                <h1 class="text-title-content text-center mb-5 md:mb-10">{{ item.value }}</h1>
            </template>

            <div class="grid grid-cols-3 md:grid-cols-5 gap-3 xl:gap-[20px]">
                <template v-for="(item, index) in benefit">
                    <div
                        class="flex flex-col justify-start items-center gap-y-2 p-4 bg-grayWhiteColor border border-primaryColor/10">
                        <img class="h-8 md:h-10" :src="getImage(item.image_thumbnail)" alt="Icons Careers">
                        <h2 class="text-[14px] md:text-primary text-grayBlackColor leading-[24px] md:leading-7 font-bold text-center font-kantumruy-pro-hanuman-sans md:line-clamp-2">
                            {{ item.title }}
                        </h2>
                    </div>
                </template>
            </div>
        </section>

        <section class="w-full bg-grayWhiteColor">
            <div class="content-body content-padding py-5 md:py-10">
                <div class="flex flex-col justify-center items-center gap-y-2 mb-5 md:mb-10">
                    <template v-for="item in settingDevelopment">
                        <h1 class="text-title-content text-center">{{ item.value }}</h1>
                        <p class="text-description text-center">{{ item.description }}</p>
                    </template>
                </div>

                <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 xl:gap-[20px]">
                    <template v-for="(item, index) in development" :key="index">
                        <CreateSolutionSection :title="item.title" :description="item.description"
                            :icon="item.image_thumbnail" />
                    </template>
                </div>
            </div>
        </section>

        <section class="content-body content-padding py-5 md:py-10">
            <div class="w-full flex justify-between items-start md:items-center bg-gradient-to-r from-[#5773E8] via-[#5648E0] to-[#5648E0] p-7 gap-5 rounded-xl"
                :class="{
                    'flex-col': isMobile
                }">
                <div class="flex flex-col justify-start gap-y-2">
                    <template v-for="item in yourCv">
                        <h1 class="text-title-content-sub text-white">{{ item.value }}</h1>
                        <p class="text-description text-white">{{ item.description }}</p>
                    </template>
                </div>

                <ButtonSendCV class="block" :url="'info@988tech.com'">
                    ផ្ញើ CV របស់អ្នក
                    <i class="ri-arrow-right-line"></i>
                </ButtonSendCV>
            </div>
        </section>
    </App>
</template>

<style scoped>
@media (min-width: 641px) {
    button:hover {
        background-color: #f3f4f6;
    }
}
</style>
